import {
  HD_PLANET_ORDER_STANDARD,
  HdPlanetKey,
  HdPlanetType,
  getHdPlanetDisplayConfig,
} from '../chart/bodygraph/planet-grid/hd-planet-display-config';

/**
 * Gedeelde legende-/filter-categorieën van de transits-feature (Nu & Straks
 * én ephemeris-lanes — besluit filterlegende): zon en maan zijn eigen items
 * (geel resp. amber — niet meer samen "Lichten"); aarde (grounding) en de
 * nodes (inner-personal-position) delen één "Posities"-item (zelfde blauw);
 * de rest volgt de cheat-sheet categorie.
 */
export type HdTransitLegendCategory =
  | 'sun'
  | 'moon'
  | 'personal'
  | 'positions'
  | 'outer-social'
  | 'outer-trans'
  | 'other';

/**
 * Beeldt een planeet af op zijn legenda-categorie: zon en maan op key, de
 * overige inner-personal planeten samen onder "Persoonlijk", aarde + nodes
 * samen onder "Posities"; de rest valt terug op de config-categorie.
 */
export function legendCategoryOf(planetKey: HdPlanetKey, planetType: HdPlanetType): HdTransitLegendCategory {
  if (planetKey === 'sun') return 'sun';
  if (planetKey === 'moon') return 'moon';
  switch (planetType) {
    case 'inner-personal':
      return 'personal';
    case 'grounding':
    case 'inner-personal-position':
      return 'positions';
    case 'outer-social':
    case 'outer-trans':
    case 'other':
      return planetType;
    default:
      // 'light' komt alleen bij de zon voor en is hierboven al op key
      // afgevangen — verdedigende fallback.
      return 'other';
  }
}

/** Convenience-variant die zelf de display-config opzoekt. */
export function legendCategoryOfKey(planetKey: HdPlanetKey): HdTransitLegendCategory {
  return legendCategoryOf(planetKey, getHdPlanetDisplayConfig(planetKey).type);
}

/**
 * Legenda-volgorde: het eerste voorkomen van elke categorie in de standaard
 * planeetvolgorde, zodat de legende dezelfde leesrichting volgt als de rijen
 * en de lanes. Alleen aanwezige categorieën worden getoond.
 */
export const HD_TRANSIT_LEGEND_CATEGORY_ORDER: readonly HdTransitLegendCategory[] = (
  [...HD_PLANET_ORDER_STANDARD, 'chiron'] as readonly HdPlanetKey[]
).reduce<HdTransitLegendCategory[]>((order, planetKey) => {
  const category = legendCategoryOfKey(planetKey);
  if (!order.includes(category)) order.push(category);
  return order;
}, []);

/** i18n-sleutel (onder `panel.legend`) per legenda-categorie. */
export const HD_TRANSIT_LEGEND_LABEL_KEYS: Readonly<Record<HdTransitLegendCategory, string>> = {
  sun: 'legend.sun',
  moon: 'legend.moon',
  personal: 'legend.personal',
  positions: 'legend.positions',
  'outer-social': 'legend.outerSocial',
  'outer-trans': 'legend.outerTrans',
  other: 'legend.other',
};
